import api from "@/services/api";
import denormalize from '@/utils/denormalizeResponse';
import _ from 'lodash';
import windowUtils from '@/utils/window-utils';

export const hydrateRoute = async (
    {commit, state, dispatch},
    {route, programId}
) => {
    // route hydration
};

/**
 * Open the application-wide edit modal.
 *
 * @param commit
 */
export const setEditModalOpen = ({commit}) => {
    commit('SET_SHOW_NEED_EDIT_MODAL', true);
}

/**
 * Close the application-wide edit modal.
 *
 * @param commit
 */
export const setEditModalClosed = ({commit}) => {
    commit('SET_SHOW_NEED_EDIT_MODAL', false);
}
/**
 * Open the application-wide edit modal.
 *
 * @param commit
 */
export const setConfirmModalOpen = ({commit}) => {
    commit('SET_SHOW_CONFIRM', true);
}

/**
 * Close the application-wide edit modal.
 *
 * @param commit
 */
export const setConfirmModalClosed = ({commit}) => {
    commit('SET_SHOW_CONFIRM', false);
}

/**
 * Set the need that is being actively manipulated.
 *
 * @param commit
 * @param state
 * @param activeNeed
 */
export const setActiveNeed = ({commit, state}, activeNeed) => {
    commit('SET_ACTIVE_NEED', activeNeed);
}

/**
 * Fetch data for the current program.
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param programId
 * @returns {Promise<*>}
 */
export async function fetchProgram({commit, state, dispatch}, programId) {
    commit('isFetchingProgram');
    return api.get(`/api/v2/resources/programs/${programId}`, {
        "query": {
            "include": {
                "id": true,
                "fullUrl": true,
                "positions": true,
                "gender": true,
                "program_need_criteria_fields": true,
                "isFavorited": true,
                "sport": {
                    "sportFields": true,
                },
                "college": {
                    "name": true,
                    "images": {
                        "url": true,
                        "isProfile": true,
                    },
                },
                "needs": {
                    "id": true,
                    "grad_year": true,
                    "sport_option_id": true,
                    "position": true,
                    "notes": true,
                    "published_at": true,
                    "match_status": true,
                    "criteria": {
                        "id": true,
                        "field_type": true,
                        "field_name": true,
                        "field_id": true,
                        "field_label": true,
                        "field_unit": true,
                        "value": true,
                        "min": true,
                        "max": true,
                        "match_status": true
                    }
                }
            }
        }
    },{headers:{"Cache-Control": "no-cache"}}).then(response => {
        commit("SET_PROGRAM", denormalize(response).resources.programs[programId]);
        const programs = denormalize(response).resources.programs[programId];
        programs?.needs?.sort((a, b) => {
            if (a.grad_year === b.grad_year) {
                return b.published_at - a.published_at;
            }
            return a.grad_year > b.grad_year ? 1 : -1;
        });
        commit("SET_PROGRAM", programs);
        commit('finishedFetchingProgram');
    });
}

export async function createNewNeeds({state, getters}, needs) {
    let data = [];

    // data is passed in the format of `id.year`
    needs.forEach((need) => {

        // Skip existing needs
        if (getters.checkNeedExists(need)) {
            return;
        }

        const item = {
            'sport_option_id': _.split(need, '.')[0],
            'grad_year': _.split(need, '.')[1],
        };
        data.push(item);
    });

    return api.post(`api/v2/programs/${state.program.id}/needs`, data);
}

export async function publishNeeds({commit, state, dispatch}) {
    commit("SET_IS_PUBLISHING_NEEDS", true);

    const unpublished = _.filter(state.program.needs, (n) => { return n.published_at === null; });
    const ids = _.map(unpublished, 'id');

    return api.post(`api/v2/programs/${state.program.id}/needs/publish`, {
        needs: ids,
    })
        .then(() => {
            commit("SET_IS_PUBLISHING_NEEDS", false);

            dispatch('redirectToNeeds');
        })
        .catch(() => {
            commit("SET_IS_PUBLISHING_NEEDS", false);
        });
}

export async function updateNeedCriteria({commit, state}, params) {
    commit("SET_IS_SAVING_CRITERIA", true);
    
    return api.put(`api/v2/programs/${state.program.id}/needs/${params.needId}`, params.data)
        .then(() => {
            commit("SET_IS_SAVING_CRITERIA", false);
        })
        .catch(() => {
            commit("SET_IS_SAVING_CRITERIA", false);
        });
}

export async function deleteNeed({commit, state}, needId) {
    commit("SET_IS_DELETING_NEED", true);
    
    return api.delete(`api/v2/programs/${state.program.id}/needs/${needId}`)
        .then(() => {
            commit("SET_IS_DELETING_NEED", false);
        })
        .catch(() => {
            commit("SET_IS_DELETING_NEED", false);
        });
};

/**
 * Pop a newly created need to the top of the stack.
 * @param state
 * @param commit
 * @param need
 */
export function popNewNeed({state, commit}, need) {
    const data = {
        need,
        ...state.program.needs
    };
    commit('POP_NEW_NEED', data);
}

export function setCheckedNeeds({commit}, checked) {
    commit('UPDATE_CHECKED_NEEDS', checked);
}

export function updateCoachSelectedNeeds({commit, state}) {
    let data = this.coachSelectedNeeds;

    commit('SET_SELECTED_NEEDS', data);
}

export function setInitialCheckedNeeds({commit, state}) {
    const initialChecked =  state.program.needs.map((need) => {
        return `${need.sport_option_id}.${need.grad_year}`;
    });
    commit('UPDATE_CHECKED_NEEDS', initialChecked);
}

export const redirectToNeeds = ({ commit }) => {
    commit('SET_IS_REDIRECTING', true);
    windowUtils.location().href = '/recruits/needs';
};